/* Scroll-scrubbed frame sequence for sections/tplate-layers-scroll.liquid (built as a spike on 2026-09-24, live since 2026-09-25). */
class TplateLayers extends HTMLElement {
  /** @type {string[]} */
  urls = [];
  /** @type {(HTMLImageElement | undefined)[]} */
  frames = [];
  hold = 0.15;
  shown = -1;
  raf = 0;
  /** @type {HTMLCanvasElement | null} */
  canvas = null;
  /** @type {CanvasRenderingContext2D | null} */
  context = null;
  /** @type {IntersectionObserver | undefined} */
  observer;

  onScroll = () => {
    if (!this.raf) {
      this.raf = requestAnimationFrame(() => {
        this.raf = 0;
        this.update();
      });
    }
  };

  connectedCallback() {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const first = this.dataset.first ?? '';
    const count = Number(this.dataset.count ?? 0);
    this.urls = Array.from({ length: count }, (_, i) =>
      first.replace(/001(\.webp)/, `${String(i + 1).padStart(3, '0')}$1`)
    );
    this.hold = Number(this.dataset.hold ?? 0.15);
    this.frames = new Array(count);
    this.canvas = this.querySelector('canvas');
    this.context = this.canvas?.getContext('2d') ?? null;
    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.observer?.disconnect();
          this.load();
        }
      },
      { rootMargin: '150% 0px' }
    );
    this.observer.observe(this);
    addEventListener('scroll', this.onScroll, { passive: true });
    addEventListener('resize', this.onScroll, { passive: true });
    this.update();
  }

  disconnectedCallback() {
    removeEventListener('scroll', this.onScroll);
    removeEventListener('resize', this.onScroll);
    this.observer?.disconnect();
  }

  /* Coarse-to-fine order: every 12th frame first, so scrubbing works early and sharpens as the rest arrive. */
  load() {
    const total = this.urls.length;
    /** @type {number[]} */
    const order = [];
    const queued = new Set();
    for (const stride of [12, 6, 3, 1]) {
      for (let i = 0; i < total; i += stride) {
        if (!queued.has(i)) {
          queued.add(i);
          order.push(i);
        }
      }
    }
    let next = 0;
    const pump = () => {
      const index = order[next++];
      if (index === undefined) return;
      const image = new Image();
      image.decoding = 'async';
      image.onload = () => {
        this.frames[index] = image;
        this.update();
        pump();
      };
      image.onerror = pump;
      image.src = this.urls[index] ?? '';
    };
    for (let lane = 0; lane < 4; lane++) pump();
  }

  update() {
    const box = this.getBoundingClientRect();
    const track = box.height - innerHeight;
    const progress = track > 0 ? Math.min(1, Math.max(0, -box.top / track)) : 0;
    const animation = Math.min(1, progress / (1 - this.hold));
    const total = this.urls.length;
    const wanted = Math.round(animation * (total - 1));
    let best = -1;
    for (let offset = 0; offset < total && best < 0; offset++) {
      if (this.frames[wanted - offset]) best = wanted - offset;
      else if (this.frames[wanted + offset]) best = wanted + offset;
    }
    if (best >= 0 && best !== this.shown) this.draw(best);
  }

  /** @param {number} index */
  draw(index) {
    const image = this.frames[index];
    const canvas = this.canvas;
    const context = this.context;
    if (!image || !canvas || !context) return;
    if (canvas.width !== image.naturalWidth) {
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
    }
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0);
    this.shown = index;
    this.classList.add('is-ready');
  }
}

if (!customElements.get('tplate-layers')) customElements.define('tplate-layers', TplateLayers);
